import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { editPlan } from "../redux/slices/plansSlice";
import Swal from 'sweetalert2';
const EditPlan = ({ plan }) => {
  // Inicializar beneficios con valores predeterminados si no existen
  const initialBenefits = plan.benefits || {
    streaming4K: false,
    offlineViewing: false,
    exclusiveContent: false
  };

  const [formData, setFormData] = useState({
    name: plan.name || "",
    detail: plan.detail || "",
    price: plan.price || 0,
    referralLimit: plan.referralLimit || 0,
    deviceLimit: plan.deviceLimit || 1,
    benefits: {
      streaming4K: initialBenefits.streaming4K || false,
      offlineViewing: initialBenefits.offlineViewing || false,
      exclusiveContent: initialBenefits.exclusiveContent || false
    }
  });

  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('benefit-')) {
      const benefitName = name.replace('benefit-', '');
      setFormData({
        ...formData,
        benefits: {
          ...formData.benefits,
          [benefitName]: checked
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "", // Limpia error al escribir
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "El nombre es obligatorio";
    if (!formData.detail.trim()) newErrors.detail = "El detalle es obligatorio";
    if (!formData.price || formData.price <= 0) newErrors.price = "Debe ser un precio válido";
    if (formData.referralLimit < 0) newErrors.referralLimit = "Debe ser un número válido";
    if (formData.deviceLimit < 0) newErrors.deviceLimit = "Debe ser un número válido";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const res = await dispatch(editPlan({ planData: formData, id: plan.id })).unwrap();
  await Swal.fire({
        title: '¡Éxito!',
        text: 'Se ha editado el plan correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error al actualizar el plan:", error);
      
      let errorMessage = "Error desconocido";
      
      if (error.status === 403) {
        errorMessage = `
          ${error.message}
          \nRol requerido: ${error.requiredRoles.join(", ")}
          \nPor favor, contacta al administrador del sistema.
        `;
      } else {
        errorMessage = error.message || "Error al actualizar el plan";
      }
      
      alert(errorMessage);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      detail: "",
      price: 0,
      referralLimit: 0,
      deviceLimit: 1,
      benefits: {
        streaming4K: false,
        offlineViewing: false,
        exclusiveContent: false
      }
    });
    setErrors({});
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-transparent py-10 px-4">
      <form
        onSubmit={handleSubmit}
        noValidate
        className="w-full max-w-2xl bg-gray-900 shadow-2xl rounded-2xl p-10 space-y-6 border border-gray-800"
      >
        <h2 className="text-3xl font-bold text-center text-white mb-4">
          ✨ Editar Plan
        </h2>

        {/* Nombre con opciones predefinidas y campo de texto */}
        <div>
          <label className="block mb-1 text-sm font-semibold text-white">Nombre:</label>
          <div className="relative">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              list="edit-plan-names"
              placeholder="Selecciona o escribe un nombre"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 text-white bg-gray-900 
                border-gray-800 focus:ring-yellow-400"
            />
            <datalist id="edit-plan-names">
              <option value="Fan Épico" />
              <option value="Crítico Épico" />
              <option value="Productor Épico" />
            </datalist>
          </div>
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Detalle */}
        <div>
          <label className="block mb-1 text-sm font-semibold text-white">Detalle:</label>
          <textarea
            name="detail"
            value={formData.detail}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-800 rounded-lg resize-none h-28 focus:outline-none focus:ring-2 focus:ring-yellow-400 text-white bg-gray-900"
          />
          {errors.detail && <p className="text-red-500 text-sm mt-1">{errors.detail}</p>}
        </div>

        {/* Precio */}
        <div>
          <label className="block mb-1 text-sm font-semibold text-white">Precio:</label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 text-white bg-gray-900 
              border-gray-800 focus:ring-yellow-400"
          />
          {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
        </div>

        {/* Límite de Referidos */}
        <div>
          <label className="block mb-1 text-sm font-semibold text-white">Límite de Referidos:</label>
          <input
            type="number"
            name="referralLimit"
            value={formData.referralLimit}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 text-white bg-gray-900 
              border-gray-800 focus:ring-yellow-400"
          />
          {errors.referralLimit && <p className="text-red-500 text-sm mt-1">{errors.referralLimit}</p>}
        </div>

        {/* Límite de Dispositivos */}
        <div>
          <label className="block mb-1 text-sm font-semibold text-white">Límite de Dispositivos:</label>
          <input
            type="number"
            name="deviceLimit"
            value={formData.deviceLimit}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 text-white bg-gray-900 
              border-gray-800 focus:ring-yellow-400"
          />
          {errors.deviceLimit && <p className="text-red-500 text-sm mt-1">{errors.deviceLimit}</p>}
        </div>

        {/* Beneficios */}
        <div className="space-y-4">
          <label className="block mb-1 text-sm font-semibold text-white">Beneficios:</label>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="benefit-streaming4K"
              checked={formData.benefits?.streaming4K || false}
              onChange={handleChange}
              className="w-5 h-5 accent-yellow-400"
            />
            <span className="text-white font-medium">Streaming en 4K</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="benefit-offlineViewing"
              checked={formData.benefits?.offlineViewing || false}
              onChange={handleChange}
              className="w-5 h-5 accent-yellow-400"
            />
            <span className="text-white font-medium">Visualización sin conexión</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="benefit-exclusiveContent"
              checked={formData.benefits?.exclusiveContent || false}
              onChange={handleChange}
              className="w-5 h-5 accent-yellow-400"
            />
            <span className="text-white font-medium">Contenido exclusivo</span>
          </div>
        </div>

        {/* Botones */}
        <div className="flex space-x-4">
          <button
            type="submit"
            className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition duration-300"
          >
            💾 Guardar Cambios
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 border-2 border-yellow-500 text-yellow-400 font-semibold py-3 rounded-lg shadow-md hover:bg-yellow-600 hover:text-gray-900 transition duration-300"
          >
            🧹 Limpiar
          </button>
        </div>
      </form>
    </section>
  );
};

export default EditPlan;
